/**
 * API client for the Facilitator Platform.
 * Uses session tokens (NOT Cognito) for authentication.
 * Automatically refreshes the token at 80% of its TTL.
 */

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:4000';

/* ------------------------------------------------------------------ */
/*  Token state                                                       */
/* ------------------------------------------------------------------ */

let sessionToken: string | null = null;
let tokenExpiresAt: number = 0; // epoch ms
let refreshTimerId: ReturnType<typeof setTimeout> | null = null;
let sessionId: string | null = null;

export function getSessionToken(): string | null {
  return sessionToken;
}

export function getSessionId(): string | null {
  return sessionId;
}

function setToken(token: string, expiresInSeconds: number, sid: string) {
  sessionToken = token;
  sessionId = sid;
  tokenExpiresAt = Date.now() + expiresInSeconds * 1000;

  // Schedule refresh at 80% TTL
  if (refreshTimerId) clearTimeout(refreshTimerId);
  const refreshDelay = expiresInSeconds * 0.8 * 1000;
  refreshTimerId = setTimeout(() => {
    refreshToken().catch(console.error);
  }, refreshDelay);
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${body}`);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}

/* ------------------------------------------------------------------ */
/*  Public API functions                                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Zoom authorization outcomes                                        */
/* ------------------------------------------------------------------ */

/** The meeting has a linked session and the caller holds a token for it. */
export interface LinkedSession {
  status: 'linked';
  sessionToken: string;
  expiresIn: number;
  sessionId: string;
  role: string;
  sessionTitle: string;
}

/**
 * Nothing is linked to this meeting yet. `canLink` is true only for a caller
 * the server verified as the meeting host AND whose Zoom account is connected
 * to a Facilitator Tools user; `hostToken` then unlocks the session picker.
 */
export interface UnlinkedMeeting {
  status: 'unlinked';
  canLink: boolean;
  isHost: boolean;
  hostToken?: string;
  expiresIn?: number;
  displayName?: string;
}

export type ExchangeOutcome = LinkedSession | UnlinkedMeeting;

/**
 * Exchange a Zoom PKCE authorization code for a Facilitator session token.
 * The backend performs the code→token exchange (it holds the Zoom client
 * secret), verifies the caller's identity, and issues a session-scoped JWT.
 * When no session is linked to the meeting it answers `status: 'unlinked'`
 * instead of an error (see UnlinkedMeeting).
 */
export async function exchangeToken(
  zoomCode: string,
  codeVerifier: string,
  meetingId: string,
  role: string,
): Promise<ExchangeOutcome> {
  const data = await apiFetch<
    Omit<LinkedSession, 'status'> & { status?: string } | UnlinkedMeeting
  >('/api/integrations/exchange-token', {
    method: 'POST',
    body: JSON.stringify({
      platform: 'zoom',
      code: zoomCode,
      codeVerifier,
      meetingContext: { meetingId, role },
    }),
  });

  if (data.status === 'unlinked') return data as UnlinkedMeeting;

  const linked = data as Omit<LinkedSession, 'status'>;
  setToken(linked.sessionToken, linked.expiresIn, linked.sessionId);
  return { status: 'linked', ...linked };
}

/** One of the host's sessions, as offered by the in-meeting picker. */
export interface HostSessionSummary {
  id: string;
  title: string;
  status: string;
  updatedAt: string | null;
  activityCount: number;
}

/** List the sessions the verified host may run in this meeting. */
export async function listHostSessions(hostToken: string): Promise<HostSessionSummary[]> {
  const data = await apiFetch<{ meetingId: string; sessions: HostSessionSummary[] }>(
    '/api/integrations/zoom/host/sessions',
    { headers: { Authorization: `Bearer ${hostToken}` } },
  );
  return data.sessions;
}

/**
 * Link one of the host's sessions to this meeting. The server answers with a
 * facilitator session token (same shape as a linked exchange), which is
 * stored here so the app can carry straight on.
 */
export async function linkHostSession(
  hostToken: string,
  sessionId: string,
): Promise<LinkedSession> {
  const data = await apiFetch<Omit<LinkedSession, 'status'>>(
    '/api/integrations/zoom/host/link',
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${hostToken}` },
      body: JSON.stringify({ sessionId }),
    },
  );
  setToken(data.sessionToken, data.expiresIn, data.sessionId);
  return { status: 'linked', ...data };
}

/**
 * Finish "Add to Zoom": hand the OAuth code from the browser redirect plus
 * the signed state from the web app to the API, which connects the Zoom
 * account to the Facilitator Tools user. Browser-only (/auth/callback).
 */
export async function completeInstall(
  code: string,
  state: string,
): Promise<{ ok: boolean; zoomDisplayName?: string }> {
  return apiFetch<{ ok: boolean; zoomDisplayName?: string }>(
    '/api/integrations/zoom/complete-install',
    { method: 'POST', body: JSON.stringify({ code, state }) },
  );
}

/**
 * Refresh the current session token.
 */
export async function refreshToken(): Promise<void> {
  if (!sessionToken || !sessionId) return;

  const data = await apiFetch<{ sessionToken: string; expiresIn: number }>(
    '/api/integrations/refresh-token',
    { method: 'POST' },
  );

  setToken(data.sessionToken, data.expiresIn, sessionId);
}

/**
 * Get all nodes for a session (requires auth).
 */
export async function getSessionNodes(sid: string): Promise<SessionNode[]> {
  return apiFetch<SessionNode[]>(`/api/sessions/${sid}/nodes`);
}

/**
 * Get public node list (no auth required, limited fields).
 */
export async function getNodesPublic(sid: string): Promise<PublicNode[]> {
  return apiFetch<PublicNode[]>(`/api/sessions/${sid}/nodes-public`);
}

/**
 * Advance to the next node.
 */
export async function advanceNode(sid: string): Promise<{ activeNodeIndex: number }> {
  return apiFetch<{ activeNodeIndex: number }>(`/api/sessions/${sid}/node-advance`, {
    method: 'POST',
  });
}

/**
 * Go to the previous node.
 */
export async function previousNode(sid: string): Promise<{ activeNodeIndex: number }> {
  return apiFetch<{ activeNodeIndex: number }>(`/api/sessions/${sid}/node-previous`, {
    method: 'POST',
  });
}

/**
 * Jump to a specific node index.
 */
export async function gotoNode(
  sid: string,
  index: number,
): Promise<{ activeNodeIndex: number }> {
  return apiFetch<{ activeNodeIndex: number }>(`/api/sessions/${sid}/node-goto`, {
    method: 'POST',
    body: JSON.stringify({ index }),
  });
}

/**
 * Quiz question flow (facilitator/session-token). A quiz is ONE session node
 * containing multiple questions, so the session-level node advance skips its
 * internal questions — the facilitator drives questions with these instead:
 * reveal the current answer, then advance to the next question (which completes
 * + deactivates the quiz on the last one).
 */
export async function revealQuiz(quizId: string): Promise<void> {
  await apiFetch(`/api/quizzes/${quizId}/reveal`, { method: 'PATCH' });
}

export async function quizNextQuestion(
  quizId: string,
): Promise<{ completed?: boolean }> {
  return apiFetch<{ completed?: boolean }>(`/api/quizzes/${quizId}/next-question`, {
    method: 'PATCH',
  });
}

/**
 * Generate (or regenerate) the AI summary for an open question. Facilitator
 * action (POST /summarise is session-token gated); the cached result is then
 * readable by everyone via getQuestionSummary.
 */
export async function generateQuestionSummary(questionId: string): Promise<void> {
  await apiFetch(`/api/questions/${questionId}/summarise`, { method: 'POST' });
}

/** Generate the AI summary for a SWOT/framework activity (facilitator). */
export async function generateSwotSummary(id: string): Promise<void> {
  await apiFetch(`/api/swot-analyses/${id}/summarise`, { method: 'POST' });
}

/** Extract the server's error message from an apiFetch error ("API 400: {json}"). */
export function apiErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  const brace = raw.indexOf('{');
  if (brace >= 0) {
    try {
      const parsed = JSON.parse(raw.slice(brace));
      if (parsed?.error) return parsed.error;
    } catch {
      /* fall through */
    }
  }
  return raw;
}

/**
 * Current session state incl. the active activity's server timer (synced
 * countdown). Participant-token readable.
 */
export async function getSessionState(sid: string): Promise<{
  activeNodeIndex: number;
  timerExpiresAt?: string;
  timerStartedAt?: string;
  timerDurationMinutes?: number;
}> {
  return apiFetch(`/api/sessions/${sid}/state`);
}

/**
 * Register a participant in the session.
 */
export async function registerParticipant(
  sid: string,
  participant: { displayName: string; zoomUserId?: string },
): Promise<{ participantId: string }> {
  return apiFetch<{ participantId: string }>(
    `/api/participants/${sid}`,
    {
      method: 'POST',
      body: JSON.stringify(participant),
    },
  );
}

/**
 * Sync the Zoom meeting roster with the session.
 */
export async function syncRoster(
  sid: string,
  roster: Array<{ zoomUserId: string; displayName: string }>,
): Promise<void> {
  await apiFetch(`/api/participants/${sid}/sync`, {
    method: 'POST',
    body: JSON.stringify({ roster }),
  });
}

/**
 * Get current active node index (used for polling fallback).
 */
export async function getActiveNodeIndex(sid: string): Promise<{ activeNodeIndex: number }> {
  return apiFetch<{ activeNodeIndex: number }>(`/api/sessions/${sid}/active-node`);
}

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

/** All activity types the platform supports (slide/exam included). */
export type NodeType =
  | 'question'
  | 'poll'
  | 'quiz'
  | 'nps'
  | 'crosstab'
  | 'imageactivity'
  | 'wordcloud'
  | 'swotanalysis'
  | 'slide'
  | 'exam';

/**
 * Shape actually returned by GET /api/sessions/:id/nodes — the activity's
 * own fields live in `data` (redacted for participants).
 */
export interface SessionNode {
  index: number;
  type: NodeType | string;
  id: string;
  data: Record<string, any> | null;
  isActive: boolean;
}

/** Display title for a node, mirroring the backend's precedence. */
export function nodeTitle(node: SessionNode): string {
  const d = node.data || {};
  return (
    d.promptText || d.question || d.title || d.name || d.text ||
    `${node.type} ${node.index + 1}`
  );
}

/** Timer duration (minutes) if the activity has one. */
export function nodeTimerMinutes(node: SessionNode): number | undefined {
  return node.data?.timerDurationMinutes || undefined;
}

export interface PublicNode {
  index: number;
  type: string;
  id: string;
  title: string;
}

/* ------------------------------------------------------------------ */
/*  Cleanup                                                           */
/* ------------------------------------------------------------------ */

export function clearAuth() {
  sessionToken = null;
  sessionId = null;
  tokenExpiresAt = 0;
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
}
